import { useMemo, useRef, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Link } from "wouter";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { useTabDraft } from "@/hooks/useTabDraft";

import { markdownToHtml, htmlToMarkdown } from "./mdConvert";
import { copyText } from "@/lib/copyText";
export { markdownToHtml, htmlToMarkdown };

const TOOL_INFO_CONTENT = {
  what: `Converts text between Markdown and HTML in either direction, useful for writers who draft in Markdown but need HTML for a website or CMS, or the reverse — pulling HTML content into clean Markdown for easier editing.`,
  how: `Paste your content, choose the conversion direction, and copy the converted output.`,
  tip: `Conversion follows CommonMark plus GitHub-style tables, task lists and strikethrough, with nested lists, quotes and code blocks. The HTML is cleaned (scripts, event handlers and javascript: links are removed). Raw HTML pasted inside Markdown is kept but sanitised; check unusual layouts after converting.`,
};

export default function MarkdownHtmlConverter() {
  const [direction, setDirection] = useState<"md-to-html" | "html-to-md">(
    "md-to-html"
  );
  const [input, setInput] = useTabDraft(
    "markdown-html-converter",
    "# Hello World\n\nThis is **bold** and *italic* text with a [link](https://example.com)."
  );

  const [fullDoc, setFullDoc] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const body = useMemo(() => (direction === "md-to-html" ? markdownToHtml(input) : htmlToMarkdown(input)), [input, direction]);
  const output = useMemo(
    () => (direction === "md-to-html" && fullDoc
      ? `<!DOCTYPE html>\n<html lang="en">\n<head>\n<meta charset="utf-8">\n<meta name="viewport" content="width=device-width, initial-scale=1">\n<title>Document</title>\n</head>\n<body>\n${body}\n</body>\n</html>\n`
      : body),
    [body, direction, fullDoc]
  );
  // The preview shows the already-sanitised HTML in a sandboxed frame with no scripts allowed.
  const previewDoc = direction === "md-to-html" ? `<base target="_blank"><style>body{font:14px system-ui,sans-serif;padding:8px}img{max-width:100%}table{border-collapse:collapse}td,th{border:1px solid #8884;padding:4px 8px}pre{background:#8882;padding:8px;overflow:auto}blockquote{border-left:3px solid #8886;margin:0;padding-left:10px}</style>${body}` : "";

  const download = () => {
    const isHtml = direction === "md-to-html";
    downloadBlob(new Blob([output], { type: isHtml ? "text/html" : "text/markdown" }), isHtml ? "converted.html" : "converted.md");
  };

  const upload = async (f?: File) => {
    if (!f) return;
    if (f.size > 2_000_000) { toast.error("That file is over 2 MB."); return; }
    const t = await f.text();
    setDirection(/\.html?$/i.test(f.name) ? "html-to-md" : "md-to-html");
    setInput(t);
  };

  const copy = () => {
    void copyText(output, "Copied to clipboard");
  };

  const swap = () => {
    setDirection(d => (d === "md-to-html" ? "html-to-md" : "md-to-html"));
    setInput(output);
  };

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });


  return (
    <ToolContainer>
      <div className="flex items-center gap-3 mb-4">
        <Button
          size="sm"
          variant={direction === "md-to-html" ? "default" : "outline"}
          onClick={() => setDirection("md-to-html")}
        >
          Markdown → HTML
        </Button>
        <Button
          size="sm"
          variant={direction === "html-to-md" ? "default" : "outline"}
          onClick={() => setDirection("html-to-md")}
        >
          HTML → Markdown
        </Button>
        <Button size="sm" variant="ghost" onClick={swap}>
          ⇅ Swap
        </Button>
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>Open .md / .html file</Button>
        <input ref={fileRef} type="file" accept=".md,.markdown,.txt,.html,.htm" className="hidden" onChange={e => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        {direction === "md-to-html" && (
          <div className="flex items-center gap-2"><Switch checked={fullDoc} onCheckedChange={setFullDoc} aria-label="Full HTML document" /><Label className="text-sm">Full HTML document</Label></div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="mb-2 block">
            {direction === "md-to-html" ? "Markdown" : "HTML"}
          </Label>
          <Textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            className="min-h-[280px] font-mono text-sm"
          />
        </div>
        <div>
          <Label className="mb-2 block">
            {direction === "md-to-html" ? "HTML" : "Markdown"} (output)
          </Label>
          <Textarea
            readOnly
            value={blocked ? "— blocked at today's usage limit —" : output}
            className="min-h-[280px] font-mono text-sm bg-muted/30"
          />
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={copy} disabled={!output || blocked}>Copy Output</Button>
        <Button variant="outline" onClick={download} disabled={!output || blocked}>Download</Button>
      </div>
      {direction === "md-to-html" && !blocked && output && (
        <div className="mt-4">
          <Label className="mb-2 block">Preview</Label>
          <iframe title="Rendered preview" sandbox="" srcDoc={previewDoc} className="w-full min-h-[240px] rounded border bg-white" data-testid="md-preview" />
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-3">This tool converts between Markdown and HTML. To write and edit a Markdown document with a live preview, use the <Link href="/tool/markdown-editor" className="underline">Markdown Editor</Link>.</p>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
